import React from 'react';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
} from 'recharts';

import NoDataAvailable from '../no-data-available/NoDataAvailable';

export interface BarChartDataType {
  name: string;
  value: number;
  status?: 'optimal' | 'subOptimal' | 'critical' | string;
  color?: string;
}

interface CustomBarChartProperties {
  data: BarChartDataType[];
  onBarClick?: (name: string) => void;
  showLegend?: boolean;
  layout?: 'horizontal' | 'vertical';
}

/**
 * Returns color based on status or fallback color
 * @param {string} status The performance status.
 * @param {string} color The fallback color.
 * @returns {string} The color hex code.
 */
const getBarColor = (status?: string, color?: string) => {
  if (status) {
    switch (status) {
      case 'optimal': return '#2E9E3A';
      case 'subOptimal': return '#F79009';
      case 'critical': return '#EF4444';
      default: break;
    }
  }
  return color || '#0099D6'; // Default dashboard blue
};

/**
 * CustomBarChart - A shared bar chart component with a custom legend.
 */
function CustomBarChart({
  data,
  onBarClick,
  showLegend = true,
  layout = 'vertical',
}: CustomBarChartProperties) {
  const isEmpty = data.length === 0 || data.every((item) => item.value === 0);

  // Only show status legend if at least one item has a status
  const hasStatus = data.some(item => item.status);

  const isVertical = layout === 'vertical'; // layout="vertical" means X is value, Y is category (horizontal bars)
  // In Recharts:
  // layout="horizontal" -> XAxis is category, YAxis is number (Standard Vertical Bars)
  // layout="vertical" -> XAxis is number, YAxis is category (Horizontal Bars like Station Performance)

  // Extract unique status values for the legend
  const uniqueStatuses = Array.from(new Set(data.map(item => item.status).filter(Boolean))) as string[];

  const SCROLL_THRESHOLD = 9;
  const hasManyItems = data.length > SCROLL_THRESHOLD;

  const chartHeight = isVertical && hasManyItems ? Math.max(250, data.length * 45) : '100%';
  const chartWidth = !isVertical && hasManyItems ? Math.max(300, data.length * 60) : '100%';

  let chartMinHeight: string | number = '100%';
  if (isVertical) {
    chartMinHeight = hasManyItems ? chartHeight : '200px';
  }

  const xAxisTick = (!isVertical && hasManyItems)
    ? {
      fontSize: 12,
      fill: '#64748B',
      angle: -45,
      textAnchor: 'end',
    } as any
    : { fontSize: 13, fill: '#64748B' };
  /**
   * Formats the label for the x-axis.
   * @param {string} label The label to format.
   * @returns {string} The formatted label.
   */
  const formatLabel = (label: string) => {
    if (hasManyItems && label.length > 10) {
      return `${label.substring(0, 10)}...`;
    }
    return label;
  };

  return (
    <div className="custom-bar-chart-wrap">
      {showLegend && hasStatus && (
        <div className="chart-legend">
          {uniqueStatuses.map((status, index) => (
            <div key={index} className="legend-item">
              <span
                className="dot"
                style={{ backgroundColor: getBarColor(status) }}
              />
              <span>{status}</span>
            </div>
          ))}
        </div>
      )}
      <div className={`bar-chart-scroll-container ${!isVertical && hasManyItems ? 'horizontal-scroll' : ''}`}>
        <div
          className="bar-chart-container"
          style={{
            height: isEmpty ? '100%' : chartHeight,
            width: isEmpty ? '100%' : chartWidth,
            position: 'relative',
            minHeight: chartMinHeight,
          }}
        >
          {isEmpty ? (
            <NoDataAvailable />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                layout={layout}
                margin={{
                  top: 5,
                  right: 30,
                  left: isVertical ? 30 : 0,
                  bottom: !isVertical && hasManyItems ? 60 : 5,
                }}
                barSize={20}
                onClick={(properties) => {
                  if (properties && (properties as any).activeLabel) {
                    onBarClick?.((properties as any).activeLabel);
                  }
                }}
              >
                {isVertical ? (
                  <>
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="name"
                      type="category"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 13, fill: '#64748B' }}
                      width={120}
                      tickFormatter={formatLabel}
                    />
                  </>
                ) : (
                  <>
                    <XAxis
                      dataKey="name"
                      type="category"
                      axisLine={false}
                      tickLine={false}
                      tick={xAxisTick}
                      interval={0}
                      tickFormatter={formatLabel}
                    />
                    <YAxis type="number" hide />
                  </>
                )}
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length > 0) {
                      const entry = payload[0].payload as BarChartDataType;
                      return (
                        <div className="custom-bar-tooltip">
                          <p>{`${entry.name}: ${entry.value}`}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="value" radius={isVertical ? [0, 4, 4, 0] : [4, 4, 0, 0]}>
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getBarColor(entry.status, entry.color)}
                      style={{ cursor: onBarClick ? 'pointer' : 'default' }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

CustomBarChart.defaultProps = {
  onBarClick: undefined,
  showLegend: true,
  layout: 'vertical',
};

export default CustomBarChart;
